import { useState, useEffect } from "react";

import EditModal from "./EditModal";
import LinkArrayInput from "./LinkArrayInput";
import SnsLinksInput from "./SnsLinksInput";
import SnsLinksDisplay from "./SnsLinksDisplay";
import { TextArea, Input } from "../global/FormElements";
import {
  normalizeSheetItem,
  normalizeSheetItems,
  renderTextWithLineBreaks,
  SHEET_ITEM_SPECS,
} from "../../utils/businessSheetUtils";

import "./styles/Cards.scss";

export const SectionTitle = ({
  title,
  theme,
  isEdit,
  data,
  name,
  placeholder,
  maxLength,
  handleEdit,
}) => {
  const [updatedData, setUpdatedData] = useState({
    [name]: data,
  });
 
  useEffect(() => {
    setUpdatedData({
      [name]: data,
    });
  }, [data, name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditData = async () => {
    await handleEdit(updatedData);
  };

  return (
    <div
      className={`sectionTitle ${isEdit ? "space" : ""}`}
      style={{ backgroundColor: theme ? theme : "#aaa" }}
    >
      <span>{title}</span>
      {isEdit && (
        <EditModal
          size={"sm"}
          title={title}
          handleEdit={handleEditData}
          theme={theme}
        >
          <TextArea
            placeholder={placeholder}
            maxLength={maxLength}
            value={updatedData?.[name]}
            onChange={handleChange}
            name={name}
          />
        </EditModal>
      )}
    </div>
  );
};

export const CardTitle = ({
  title,
  theme,
  name,
  handleEdit,
  data,
  placeholder,
  isEdit,
  maxLength,
}) => {
  const [updatedData, setUpdatedData] = useState({
    [name]: data,
  });

  useEffect(() => {
    setUpdatedData({
      [name]: data,
    });
  }, [data, name]);

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    data[index] = value;
    const newArray = [...data];

    setUpdatedData((prevState) => ({
      ...prevState,
      [name]: newArray,
    }));
  };

  const handleEditData = async () => {
    await handleEdit(updatedData);
  };

  return (
    <div
      className="cardTitle"
      style={{ backgroundColor: theme ? theme : "#aaa" }}
    >
      <img src={`/images/${name}.png`} alt={name} />
      <p>{title}</p>
      {isEdit && (
        <EditModal
          size={"sm"}
          title={title}
          theme={theme}
          handleEdit={handleEditData}
        >
          {data?.map((item, index) => (
            <Input
              key={index}
              index={index}
              lable={`エリア${index + 1}`}
              placeholder={placeholder}
              maxLength={maxLength}
              value={item}
              onChange={(e) => handleChange(e, index)}
              name={name}
            />
          ))}
        </EditModal>
      )}
    </div>
  );
};

export const CardTitle2 = ({
  title,
  theme,
  name,
  handleEdit,
  data,
  placeholder,
  isEdit,
  maxLength,
}) => {
  const [updatedData, setUpdatedData] = useState({
    [name]: data,
  });

  useEffect(() => {
    setUpdatedData({
      [name]: data,
    });
  }, [data, name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditData = async () => {
    await handleEdit(updatedData);
  };

  return (
    <div
      className="cardTitle"
      style={{ backgroundColor: theme ? theme : "#999999" }}
    >
      <img src={`/images/${name}.png`} alt={name} />
      <p>{title}</p>
      {isEdit && (
        <EditModal
          size={"sm"}
          title={title}
          theme={theme}
          handleEdit={handleEditData}
        >
          <TextArea
            placeholder={placeholder}
            maxLength={maxLength}
            value={updatedData?.[name]}
            onChange={handleChange}
            name={name}
          />
        </EditModal>
      )}
    </div>
  );
};

// ── Issue #91: 構造化アイテム（金のシリーズ / パワーワード / アイテム）────────

/**
 * 1件分の表示カード。
 * 金のシリーズは 左1/5に番号・右4/5を3分割、パワーワードは上下2分割、
 * アイテムは 上中下3分割（URL はリンク描画）。
 */
export const SheetItemCard = ({ kind, item, rank }) => {
  const spec = SHEET_ITEM_SPECS[kind];
  const value = normalizeSheetItem(item, kind);
  const noContent = <span className="no-content">データがありません</span>;

  const renderValue = (field) => {
    if (field.key === "url") {
      return value.url ? (
        <a
          href={value.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {value.url}
        </a>
      ) : (
        noContent
      );
    }
    return renderTextWithLineBreaks(value[field.key]) ?? noContent;
  };

  return (
    <div className={`sheetItemCard sheetItemCard--${kind}`}>
      {rank && (
        <div className="sheetItemCard__no">
          <img src={`/images/rank${rank}.png`} alt={`rank${rank}`} />
        </div>
      )}
      <div className="sheetItemCard__body">
        {spec.fields.map((field) => (
          <div className="sheetItemCard__row" key={field.key}>
            <span className="sheetItemCard__label">{field.label}</span>
            <div className="sheetItemCard__value">{renderValue(field)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** 構造化アイテムの編集入力（既定件数 × サブフィールド分の Input） */
export const StructuredItemsInput = ({ name, kind, items, onChange }) => {
  const spec = SHEET_ITEM_SPECS[kind];
  const list = normalizeSheetItems(items, kind);

  // props の配列を破壊せず、常に新しい配列を作って返す（F4）
  const handleChange = (index, key, value) => {
    onChange(
      list.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );
  };

  return (
    <div className={`structuredItems structuredItems--${kind}`}>
      {list.map((item, index) => (
        <div className="structuredItems__item" key={index}>
          <span className="structuredItems__no">{index + 1}</span>
          {spec.fields.map((field) => (
            <Input
              key={field.key}
              index={`${index}-${field.key}`}
              lable={`${index + 1}. ${field.label}`}
              placeholder={`${field.label}を入力`}
              maxLength={field.maxLength}
              value={item[field.key]}
              onChange={(e) => handleChange(index, field.key, e.target.value)}
              name={`${name}.${index}.${field.key}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

/** 構造化アイテム枠のタイトル（＋編集モーダル） */
export const StructuredCardTitle = ({
  title,
  theme,
  name,
  kind,
  data,
  isEdit,
  handleEdit,
}) => {
  const [items, setItems] = useState(() => normalizeSheetItems(data, kind));

  useEffect(() => {
    setItems(normalizeSheetItems(data, kind));
  }, [data, kind]);

  const handleEditData = async () => {
    await handleEdit({ [name]: items });
  };

  return (
    <div
      className="cardTitle"
      style={{ backgroundColor: theme ? theme : "#aaa" }}
    >
      <img src={`/images/${name}.png`} alt={name} />
      <p>{title}</p>
      {isEdit && (
        <EditModal
          size={"md"}
          title={title}
          theme={theme}
          handleEdit={handleEditData}
        >
          <StructuredItemsInput
            name={name}
            kind={kind}
            items={items}
            onChange={setItems}
          />
        </EditModal>
      )}
    </div>
  );
};

export const CardData = ({ rank, data }) => {
  return (
    <div className="valueContainer">
      {rank && <img src={`/images/rank${rank}.png`} alt={`rank${rank}`} />}
      <p>
        {renderTextWithLineBreaks(data) ?? (
          <span className="no-content">データがありません</span>
        )}
      </p>
    </div>
  );
};

export const Card = ({
  title,
  data,
  theme,
  isEdit,
  handleEdit,
  name,
  placeholder,
  maxLength,
}) => {
  return (
    <div className="card">
      <SectionTitle
        title={title}
        theme={theme}
        isEdit={isEdit}
        data={data}
        handleEdit={handleEdit}
        name={name}
        placeholder={placeholder}
        maxLength={maxLength}
      />
      <div className="cardContent">
        {renderTextWithLineBreaks(data) ?? (
          <span className="no-content">データがありません</span>
        )}
      </div>
    </div>
  );
};

export const LinksCard = ({ theme, isEdit, data, name, snsData, handleEdit }) => {
  const [links, setLinks] = useState(data || []);
  const [snsLinks, setSnsLinks] = useState(snsData || []);

  useEffect(() => {
    setLinks(data || []);
  }, [data]);

  useEffect(() => {
    setSnsLinks(snsData || []);
  }, [snsData]);

  const handleEditData = async () => {
    await handleEdit({ [name]: links, snsLinks });
  };

  const visibleLinks = (links || []).filter((l) => l.url);

  return (
    <div className="card">
      <div
        className={`sectionTitle ${isEdit ? "space" : ""}`}
        style={{ backgroundColor: theme || "#aaa" }}
      >
        <span>ウェブサイト・SNSリンク</span>
        {isEdit && (
          <EditModal
            size={"md"}
            title="ウェブサイト・SNSリンク"
            handleEdit={handleEditData}
            theme={theme}
          >
            <LinkArrayInput links={links} onChange={setLinks} />
            <SnsLinksInput snsLinks={snsLinks} onChange={setSnsLinks} />
          </EditModal>
        )}
      </div>
      <div className="cardContent linksContent">
        {visibleLinks.length > 0 ? (
          visibleLinks.map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="sheetLink"
            >
              {link.label || link.url}
            </a>
          ))
        ) : (
          <span className="no-content">データがありません</span>
        )}
        {snsLinks.length > 0 && <SnsLinksDisplay snsLinks={snsLinks} />}
      </div>
    </div>
  );
};

export const ChildCard = ({
  title,
  data,
  theme,
  isEdit,
  handleEdit,
  placeholder,
  maxLength,
  name,
}) => {
  const [showMore, setShowMore] = useState(0);

  const toggleShowMore = () => {
    setShowMore(!showMore);
  };

  const [updatedData, setUpdatedData] = useState({
    [name]: data,
  });

  useEffect(() => {
    setUpdatedData({
      [name]: data,
    });
  }, [data, name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditData = async () => {
    await handleEdit(updatedData);
  };

  return (
    <div className="ChildCard">
      <div className="title">
        <span>{title}</span>
        {isEdit && (
          <EditModal
            size={"sm"}
            title={title}
            theme={theme}
            handleEdit={handleEditData}
          >
            <TextArea
              placeholder={placeholder}
              maxLength={maxLength}
              value={updatedData?.[name]}
              onChange={handleChange}
              name={name}
            />
          </EditModal>
        )}
      </div>
      <div
        className="cardContent"
        style={showMore ? {} : { WebkitLineClamp: 4, lineClamp: 4 }}
      >
        {renderTextWithLineBreaks(data) ?? (
          <span className="no-content">データがありません</span>
        )}
      </div>
      {data && (
        <div className="showMore" onClick={toggleShowMore}>
          <img
            style={{ rotate: `${180 * !showMore}deg` }}
            src="/images/arrow.png"
            alt="arrow"
          />
          <span>{showMore ? "一部を表示" : "もっと見る"}</span>
        </div>
      )}
    </div>
  );
};